import Papa from "papaparse";

export type CsvRowOutcome = { status: "succeeded" | "recovered" | "failed" | "unattempted" | "unknown"; error?: string; row?: number; originalRow?: number; recoveryId?: string; recoveryEventId?: string; initialError?: string; latestError?: string };

/** Read new row annotations, retaining compatibility with older error CSVs. */
export const getCsvRowOutcome = (row: Record<string, string>): CsvRowOutcome | undefined => {
  if (row.__deci) {
    try {
      const value = JSON.parse(row.__deci);
      if (["succeeded", "recovered", "failed", "unattempted", "unknown"].includes(value?.status)) return value;
    } catch { /* An invalid annotation is never treated as a successful row. */ }
    return { status: "unknown", error: "Invalid row outcome annotation" };
  }
  return row.__error?.trim() ? { status: "failed", error: row.__error } : undefined;
};

/** Select remaining rows; the whole-file crash fallback deliberately includes unknown outcomes. */
export const buildArchivedCsvErrorSubset = (content: string, expectedRows: number, interrupted = false, noSuccessfulRows = false): string | undefined => {
  const parsed = Papa.parse<Record<string, string>>(content, { header: true, skipEmptyLines: "greedy" });
  const fields = parsed.meta.fields;
  if (parsed.errors.length || !fields) return undefined;
  if (!fields.some(field => field === "__deci" || field === "__error")) {
    if (!(interrupted || noSuccessfulRows) || !parsed.data.length || parsed.data.length !== expectedRows) return undefined;
    return Papa.unparse({ fields: [...fields, "__deci"], data: parsed.data.map((row, index) => ({ ...row,
      __deci: JSON.stringify({ status: interrupted ? "unknown" : "unattempted", row: index + 2,
        error: interrupted ? "Worker interrupted: earlier successful rows may be replayed" : "Previous processing stopped before a successful outcome was recorded" }),
    })) });
  }
  const remaining = parsed.data.filter(row => {
    const outcome = getCsvRowOutcome(row);
    return outcome?.status === "failed" || outcome?.status === "unattempted" || (interrupted && !isCsvRowSuccessful(row));
  });
  if (!remaining.length || remaining.length !== expectedRows) return undefined;
  return Papa.unparse({ fields, data: remaining });
};

export const FILE_PROCESSING_HEARTBEAT_TTL_MS = 10 * 60 * 1000;
export const fileProcessingHeartbeatKey = (container: string | undefined, path: string) => `file-processing-active:${container || "default"}:${getOriginalFileProcessingPath(path).replace(/\.[^./]+$/, "")}`;

// Match the ingestion parser's header and line-ending normalization, including quoted multiline cells.
export const parseRecoveryCsv = (content: string) => {
  const parsed = Papa.parse<Record<string, string>>(content.replace(/\r\n/g, "\n").replace(/\r/g, "\n"), {
    header: true, skipEmptyLines: "greedy", transformHeader: header => header.replace(/^\uFEFF/, "").trim(),
  });
  // A single-column CSV needs no delimiter; Papa still reports this warning.
  parsed.errors = parsed.errors.filter(error => error.code !== "UndetectableDelimiter");
  return parsed;
};

/** Only matching, explicitly confirmed rows are excluded, including an unfinished log's complete entries. */
export const getConfirmedCsvRowIndices = (content: string, rows: Record<string, any>[], headers: string[]): number[] => {
  const parsed = parseRecoveryCsv(content);
  return parsed.data.flatMap(row => {
    const outcome = getCsvRowOutcome(row);
    const index = (outcome?.row || 0) - 2;
    return isCsvRowSuccessful(row) && Number.isInteger(index) && index >= 0 && index < rows.length
      && headers.filter(header => header !== "__deci" && header !== "__error").every(header => String(row[header] ?? "") === String(rows[index][header] ?? "")) ? [index] : [];
  });
};

export const buildInterruptedCsvRecovery = (content: string, evidence: string[]) => {
  const parsed = parseRecoveryCsv(content);
  if (parsed.errors.length || !parsed.meta.fields || !parsed.data.length) throw new Error("The original CSV cannot be parsed for recovery");
  const headers = parsed.meta.fields.filter(header => header !== "__deci" && header !== "__error");
  const confirmed = new Set(evidence.flatMap(csv => getConfirmedCsvRowIndices(csv, parsed.data, headers)));
  const rows = parsed.data.map((row, index) => ({ ...row, __deci: JSON.stringify({ row: index + 2,
    status: confirmed.has(index) ? "succeeded" : "unknown",
    ...(getCsvRowOutcome(row)?.originalRow ? { originalRow: getCsvRowOutcome(row)!.originalRow } : {}),
    ...(confirmed.has(index) ? {} : { error: "Worker interrupted: outcome unknown; retry may resend successful rows" }),
  }) }));
  const fields = [...headers, "__deci"];
  const remaining = rows.filter((_, index) => !confirmed.has(index));
  return { archive: Papa.unparse({ fields, data: rows }),
    errors: remaining.length ? Papa.unparse({ fields, data: remaining }) : undefined,
    totalRows: rows.length, successfulRows: confirmed.size, remainingRows: remaining.length };
};


/** Retry attempts belong to the original CSV, including older compact timestamps. */
export const getOriginalFileProcessingPath = (path: string): string => path.replace(/(?:\.retry-(?:\d{4}-\d{2}-\d{2}T\d{2}-\d{2}-\d{2}Z|\d{8}T\d{6}Z))+?(?=\.csv$)/i, "");

export const isCsvRowSuccessful = (row: Record<string, string>): boolean => ["succeeded", "recovered"].includes(getCsvRowOutcome(row)?.status || "");

export const buildFileProcessingCsv = (fields: string[], rows: Record<string, string>[]): string => Papa.unparse({ fields, data: rows });
